import { Chart } from '@mm/components';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { selectXRayAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { abbreviateNumber } from '@mm/utils';
import classNames from 'classnames';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { SPLIT_NUMBER_BY_THOUTHANDS } from '../../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import styles from './AnalyticsCharts.module.css';

export const XRayContactsLineChart = ({
  className,
  busy,
  chartHeaderRightSide,
}: {
  className?: string;
  busy?: boolean;
  chartHeaderRightSide?: string | ReactNode;
}) => {
  const xRayAnalytics = useSelector(selectXRayAnalytics);
  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  const dates =
    xRayAnalytics.contacts_gathered?.dates?.map(date =>
      moment(date).format('MMM D'),
    ) || [];
  const contacts = xRayAnalytics.contacts_gathered?.hits || [];
  const emails = xRayAnalytics.email_sent?.hits || [];

  const totalHitsCount = contacts.reduce((prev, curr) => prev + curr, 0);
  const chartTitle = busy
    ? `${pixelName} Contacts Gathered`
    : `${pixelName} Contacts Gathered - ${totalHitsCount}`;

  const data = {
    labels: dates,
    data: [contacts, emails],
    color: ['#08979C', '#2F54EB'],
    title: ['Contacts Gathered', 'Emails Sent'],
    fill: false,
  };

  const datasetOpts = {
    pointRadius: 4,
    circleFilled: true,
  };

  const otherOpts: Chart.ChartOptions = {
    legend: {
      display: true,
      align: 'center',
      position: 'bottom',
      labels: {
        usePointStyle: true,
        boxWidth: 8,
        padding: 16,
      },
    },
  };

  const tooltipsCallbacks = {
    label: (item: Chart.ChartTooltipItem, data: Chart.ChartData) => {
      const { datasetIndex, value, label: itemLabel } = item;
      const { datasets } = data;

      if (typeof datasetIndex === 'number' && datasets) {
        const { label } = datasets[datasetIndex];

        const isEmail = label === 'Emails Sent';
        const stringifiedValue = value
          ?.toString()
          .replace(SPLIT_NUMBER_BY_THOUTHANDS, ',');
        const unit = pluralize(isEmail ? 'email' : 'contact', Number(value));
        return `${label}: ${stringifiedValue} ${unit}`;
      }
      return `${itemLabel}: ${value}`;
    },
  };

  return (
    <Chart
      className={classNames(styles.chart, className)}
      name={chartTitle}
      headerRightSide={chartHeaderRightSide}
      data={data}
      tooltipsCallbacks={tooltipsCallbacks}
      additionalOpts={{
        borderDashXAxis: [0, 10],
        borderDashYAxis: [10, 0],
        datasetOpts,
        otherOpts,
      }}
      ticksCallback={val => abbreviateNumber(val)}
      customLegend={<></>}
      busy={busy}
    />
  );
};
